import axios from 'axios'
import {API_URL, fixNumbers} from '../../../../_metronic/helpers'
import {
  BuyerTaxFileLookup,
  normalizeLookupIdentifier,
} from '../../apps/customer-management/customers-list/customer-edit-modal/buyerTaxFile'
import {StartedPayment} from '../../online-payment/core/onlinePaymentApi'

// «استعلام شناسه ملی» (user request 1405/07/30): the free taxpayer inquiry by economic code or national id, and the
// tax-file inquiry, a value-added service sold like the goods/services ID categories (backend 1.0.0.720).

/** What `POST auth/taxInqueryCompany` answers for a taxpayer. */
export type TaxpayerAnswer = {
  nId?: string | null
  ecd?: string | null
  nm?: string | null
  tp?: string | null
  st?: string | null
  /** No active business in the panel has this taxpayer yet. */
  hs?: boolean
  address?: string | null
  zipCode?: string | null
}

export type TaxFileOffer = {
  months?: number | null
  title: string
  amount: number
  listAmount?: number | null
  vatRate: number
  vat: number
  payable: number
}

export type TaxFileAccess = {
  open: boolean
  state: 'open' | 'signIn' | 'locked'
  /** administrator, business, subscription or wallet (each inquiry from the person's own wallet, backend 1.0.0.723). */
  openBy?: string | null
  reason?: string | null
  signedIn: boolean
  subscription?: {endsAt?: string | null; endsAtLabel?: string | null} | null
  business?: {
    companyId: number
    active: boolean
    servicesPath: string
    description?: string | null
  } | null
  offers: TaxFileOffer[]
  /** The personal tariff's daily cap (backend 1.0.0.723: set by the administrator; null for none). */
  dailyLimit?: number | null
  /** Today's use of the person's own subscription or wallet, when a daily cap applies. */
  daily?: TaxFileDaily | null
  /** Each inquiry paid from the person's own wallet: its fee and the balance (openBy wallet). */
  wallet?: TaxFileWallet | null
  /** The personal service is «تماس بگیرید»: the page points to support. */
  contactOnly?: boolean
}

export type TaxFileDaily = {limit: number; used: number; remaining: number}

export type TaxFileWallet = {
  fee: number
  listFee?: number | null
  balance: number
  enough: boolean
}

export type InquiryHistoryKind = 'taxpayer' | 'taxFile'

export type InquiryHistoryItem = {
  id: number
  kind: InquiryHistoryKind
  identifier: string
  title?: string | null
  createdAt?: string | null
  result?: TaxpayerAnswer | BuyerTaxFileLookup | null
}

export const TAX_FILE_LOCKED = 'TAX_FILE_INQUIRY_LOCKED'
export const TAX_FILE_DAILY_LIMIT = 'TAX_FILE_INQUIRY_DAILY_LIMIT'
export const PERSONAL_WALLET_SHORT = 'PERSONAL_WALLET_INSUFFICIENT'

export const normalizeInquiryIdentifier = (value?: string) =>
  fixNumbers(value || '').replace(/[^0-9]/g, '')

/** شناسه ملی حقوقی (۱۱) یا شماره اقتصادی (۱۱ یا ۱۴ رقم)، یا کد ملی (۱۰ رقم). */
export const isTaxpayerIdentifier = (value?: string) =>
  [10, 11, 14].includes(normalizeInquiryIdentifier(value).length)

/** The free inquiry; keepHistory writes it to the signed-in user's history. */
export const inquireTaxpayer = async (
  identifier: string,
  keepHistory: boolean
): Promise<TaxpayerAnswer> => {
  const digits = normalizeInquiryIdentifier(identifier)
  const economic = digits.length === 11 || digits.length === 14
  return (
    await axios.post<TaxpayerAnswer>(`${API_URL}/auth/taxInqueryCompany`, {
      economicCode: economic ? digits : '',
      nationalCode: economic ? '' : digits,
      keepHistory,
    })
  ).data
}

/** What opens the tax-file inquiry for the signed-in person, in a few words for the page's top bar. */
export const taxFilePlanLabel = (access?: TaxFileAccess | null): string | undefined => {
  if (!access?.open) return undefined
  if (access.openBy === 'subscription') {
    return access.subscription?.endsAtLabel
      ? `اشتراک استعلام پرونده تا ${access.subscription.endsAtLabel}`
      : 'اشتراک استعلام پرونده فعال است'
  }
  if (access.openBy === 'wallet' && access.wallet) {
    return `کیف پول شخصی: ${access.wallet.balance.toLocaleString('fa-IR')} ریال`
  }
  return access.openBy === 'business' ? 'استعلام پرونده از طریق کسب‌وکار شما' : undefined
}

export const getTaxFileAccess = async (): Promise<TaxFileAccess> =>
  (await axios.get<TaxFileAccess>(`${API_URL}/tax-file-inquiry/access`)).data

export type TaxFileLookupAnswer = {
  data: BuyerTaxFileLookup
  openBy?: string
  daily?: TaxFileDaily | null
  /** The person's wallet after the inquiry's fee (openBy wallet). */
  wallet?: TaxFileWallet | null
}

export const lookUpTaxFiles = async (identifier: string): Promise<TaxFileLookupAnswer> =>
  (
    await axios.post<TaxFileLookupAnswer>(`${API_URL}/tax-file-inquiry/lookup`, {
      identifier: normalizeLookupIdentifier(identifier),
    })
  ).data

export const startTaxFileSubscription = async (
  months: number | null | undefined,
  returnPath: string
): Promise<StartedPayment> =>
  (
    await axios.post<StartedPayment>(`${API_URL}/tax-file-inquiry/subscription`, {
      months: months ?? null,
      returnPath,
    })
  ).data

export const getInquiryHistory = async (kind?: InquiryHistoryKind): Promise<InquiryHistoryItem[]> =>
  (
    await axios.get<{items: InquiryHistoryItem[]}>(`${API_URL}/tax-file-inquiry/history`, {
      params: kind ? {kind} : {},
    })
  ).data?.items ?? []

/** The free inquiry's refusal: the server answers plain text (400 or 5xx). */
export const readTaxpayerFailure = (error: unknown): string => {
  const response = (error as {response?: {status?: number; data?: unknown}})?.response
  if (!response)
    return 'اتصال به سامانه برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.'
  const data = response.data
  if (typeof data === 'string' && data.trim()) return data.trim()
  const message =
    (data as {message?: string; Message?: string})?.message || (data as {Message?: string})?.Message
  return message?.trim() || 'استعلام انجام نشد؛ چند لحظه بعد دوباره تلاش کنید.'
}

/**
 * The tax-file inquiry's refusal: locked (sign in / subscribe), the day used up, the person's wallet short of one
 * inquiry, or a message.
 */
export const readTaxFileFailure = (
  error: unknown
): {
  locked: boolean
  dayUsedUp?: boolean
  walletShort?: boolean
  state?: string
  message: string
} => {
  const response = (
    error as {
      response?: {status?: number; data?: {code?: string; state?: string; message?: string}}
    }
  )?.response
  if (!response)
    return {
      locked: false,
      message: 'اتصال به سامانه برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.',
    }
  const data = response.data
  if (response.status === 403 && data?.code === TAX_FILE_LOCKED) {
    return {
      locked: true,
      state: data.state,
      message: data.message || 'استعلام پرونده مالیاتی سرویس ویژه است.',
    }
  }
  if (response.status === 429 && data?.code === TAX_FILE_DAILY_LIMIT) {
    return {
      locked: false,
      dayUsedUp: true,
      message: data.message || 'سقف استعلام روزانهٔ اشتراک شما پر شده است؛ فردا دوباره تلاش کنید.',
    }
  }
  if (response.status === 402 && data?.code === PERSONAL_WALLET_SHORT) {
    return {
      locked: false,
      walletShort: true,
      message: data.message || 'موجودی کیف پول شخصی کافی نیست؛ ابتدا کیف پول را شارژ کنید.',
    }
  }
  return {
    locked: false,
    message:
      data?.message?.trim() ||
      'سامانه مؤدیان به استعلام پرونده مالیاتی پاسخ نداد؛ چند لحظه بعد دوباره تلاش کنید.',
  }
}
